import type { ComponentProps, ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import getAvatarColor from '@/utils/getAvatarColor';
import getInitials from '@/utils/getInitials';
import { useCustomerPhoto } from '@/hooks/useCustomerPhoto';

type AvatarCustomer = {
  _id?: string;
  _business?: string | { _id: string };
  name?: string | null;
  phone?: string | null;
  photoUrl?: string | null;
};

type CustomerAvatarProps = ComponentProps<typeof Avatar> & {
  customer?: AvatarCustomer | null;
  fallbackClassName?: string;
  fallbackName?: string;
  colorSeed?: string;
  fallback?: ReactNode;
};

/** Shared customer identity. Failed or missing images retain the initials. */
export default function CustomerAvatar({
  customer,
  className,
  fallbackClassName,
  fallbackName,
  colorSeed,
  fallback,
  ...props
}: CustomerAvatarProps) {
  const savedCustomer = useCustomerPhoto(customer);
  const name = fallbackName ?? customer?.name ?? '';
  const photoUrl = savedCustomer?.photoUrl || undefined;

  return (
    <Avatar
      // Reset image loading state when a recycled row changes customer/photo.
      key={`${customer?._id || ''}:${photoUrl || ''}`}
      className={className}
      {...props}
    >
      {photoUrl && (
        <AvatarImage
          src={photoUrl}
          alt={customer?.name ? `${customer.name}'s photo` : 'Customer photo'}
          className="object-cover"
        />
      )}
      <AvatarFallback
        className={cn('font-semibold text-white', fallbackClassName)}
        style={{ backgroundColor: getAvatarColor(colorSeed || name || customer?._id || '') }}
      >
        {fallback ?? getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
